import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SITE } from "@/data/site";
import { MaskLines } from "@/components/site/Reveal";

const ease = [0.22, 1, 0.36, 1];

const HeroCopy = ({ align = "center" }) => (
  <div className={`flex flex-col ${align === "center" ? "items-center text-center" : "items-start text-left"}`}>
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.8, ease }}
      className="mb-5 font-display text-lg italic tracking-wide md:text-2xl"
    >
      <motion.span
        className="inline-block"
        style={{
          backgroundImage:
            "linear-gradient(100deg, #F5E9C8 0%, #EBC875 30%, #FFF7DA 48%, #FFFFFF 50%, #FFF7DA 52%, #EBC875 70%, #B7873B 100%)",
          backgroundSize: "220% 100%",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          WebkitTextFillColor: "transparent",
          filter: "drop-shadow(0 2px 20px rgba(235,200,117,0.35))",
        }}
        animate={{ backgroundPosition: ["220% 0%", "-120% 0%"] }}
        transition={{ duration: 3.2, ease: "linear", repeat: Infinity, repeatDelay: 0.5 }}
      >
        Vruksha Valley · Luxury Eco Friendly Resort
      </motion.span>
    </motion.p>

    <h1 className="font-display font-medium leading-[1.02] tracking-tight text-ivory [text-shadow:0_2px_30px_rgba(0,0,0,0.45)] text-4xl sm:text-5xl md:whitespace-nowrap md:text-6xl lg:text-7xl">
      <MaskLines lines={["Escape Into The Mist"]} delay={0.9} />
    </h1>

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.55, duration: 0.9, ease }}
      className="mt-7 max-w-xl text-base text-ivory/90 md:text-lg"
    >
      A cinematic, eco-luxury nature retreat cradled in the Western Ghats near Kalasa — timber cottages, an infinity pool and authentic Malnad warmth.
    </motion.p>

    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2.0, duration: 0.9, ease }}
      className={`mt-9 flex flex-wrap gap-4 ${align === "center" ? "justify-center" : ""}`}
    >
      <Link to="/stay" data-testid="hero-explore-btn" className="rounded-full border border-champagne/50 px-8 py-4 text-sm font-medium text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-ivory hover:text-forest">
        Explore The Resort
      </Link>
      <Link to="/contact" data-testid="hero-book-btn" className="rounded-full bg-champagne px-8 py-4 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-0.5 hover:bg-ivory">
        Book Your Stay
      </Link>
    </motion.div>
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section data-testid="hero-section" className="bg-forest">
      {/* Mobile: video on top (below navbar), copy stacked underneath */}
      <div className="md:hidden">
        <div className="w-full bg-forest pt-[68px]">
          <video className="h-auto w-full object-contain" autoPlay muted loop playsInline poster={SITE.heroImage} preload="auto">
            <source src={SITE.heroVideo} type="video/mp4" />
          </video>
        </div>
        <div className="bg-forest px-5 pb-14 pt-10">
          <HeroCopy align="left" />
        </div>
      </div>

      {/* Desktop: full-screen video (cover) with overlaid copy */}
      <div ref={ref} className="relative hidden h-screen w-full overflow-hidden bg-forest md:block">
        <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline poster={SITE.heroImage} preload="auto">
          <source src={SITE.heroVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-forest/70 via-forest/30 to-forest/85" />
        <div className="absolute inset-0 bg-black/30" />

        <motion.div style={{ opacity }} className="relative z-10 flex h-full items-center justify-center px-6">
          <HeroCopy align="center" />
        </motion.div>

        <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-champagne/80">
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
            <ChevronDown className="h-6 w-6" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
